import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  LogOut, 
  Newspaper, 
  Home,
  Search,
  MoreHorizontal,
  Video,
  FileText,
  Megaphone,
  FileEdit,
  Inbox,
  Settings as SettingsIcon
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { newsAPI, inquiriesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import Pagination from '../../components/ui/Pagination';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const categoryLabels = {
  'ekonomi': 'Ekonomi',
  'politika': 'Politika',
  'vizyon-sektor': 'Vizyon Sektör',
  'dunya': 'Dünya',
  'teknoloji': 'Teknoloji',
  'finans': 'Finans',
};

// Stable NewsRow component to prevent DOM sync issues
const NewsRow = ({ item, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <TableRow data-testid={`news-row-${item.id}`}>
      <TableCell>
        <div className="flex items-center gap-3">
          <div className="w-16 h-10 rounded overflow-hidden flex-shrink-0 bg-gray-100">
            <img
              src={item.image_url}
              alt={item.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-gray-900 truncate max-w-[300px]">
              {item.title}
            </p>
            {item.youtube_url && (
              <span className="text-xs text-purple-600 flex items-center gap-1 mt-0.5">
                <Video className="w-3 h-3" />
                Video içerik
              </span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline">
          {categoryLabels[item.category] || item.category}
        </Badge>
      </TableCell>
      <TableCell>
        {item.is_featured ? (
          <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
            Öne Çıkan
          </Badge>
        ) : (
          <Badge variant="secondary">Normal</Badge>
        )}
      </TableCell>
      <TableCell className="text-gray-500 text-sm">
        {format(new Date(item.created_at), 'd MMM yyyy', { locale: tr })}
      </TableCell>
      <TableCell className="text-right">
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid={`news-actions-${item.id}`}>
              <MoreHorizontal className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-40">
            <DropdownMenuItem 
              onClick={() => {
                setMenuOpen(false);
                onEdit(item.id);
              }}
              className="cursor-pointer"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Düzenle
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600 cursor-pointer"
              onClick={() => {
                setMenuOpen(false);
                onDelete(item);
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Sil
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};

const PAGE_SIZE = 20;

const AdminDashboard = () => {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [featuredCount, setFeaturedCount] = useState(0);
  const [videoCount, setVideoCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [unreadInquiries, setUnreadInquiries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newsToDelete, setNewsToDelete] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/giris');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchNews(currentPage, searchQuery);
      // Fetch unread inquiry count for the sidebar badge
      inquiriesAPI.unreadCount()
        .then((r) => setUnreadInquiries(r.data?.unread || 0))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentPage]);

  // Debounced search: jump back to page 1 when query changes
  useEffect(() => {
    if (!isAuthenticated) return;
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchNews(1, searchQuery);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const fetchNews = async (page, query) => {
    setLoading(true);
    try {
      if (query && query.trim().length >= 2) {
        // Search mode — show matching results (pagination hidden)
        const res = await newsAPI.search(query.trim());
        setNews(res.data || []);
        setTotalCount((res.data || []).length);
      } else {
        // Paged listing
        const skip = (page - 1) * PAGE_SIZE;
        const [listRes, countRes] = await Promise.all([
          newsAPI.getAll({ limit: PAGE_SIZE, skip }),
          newsAPI.count(),
        ]);
        setNews(listRes.data || []);
        setTotalCount(countRes.data?.total || 0);
      }
      // Stats cards: always show real global totals
      const [featRes, videoRes] = await Promise.all([
        newsAPI.count({ featured: true }),
        newsAPI.count({ category: 'vizyon-sektor' }),
      ]);
      setFeaturedCount(featRes.data?.total || 0);
      setVideoCount(videoRes.data?.total || 0);
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error('Haberler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => fetchNews(currentPage, searchQuery);

  const handleDelete = async () => {
    if (!newsToDelete) return;

    try {
      await newsAPI.delete(newsToDelete.id);
      toast.success('Haber silindi');
      // Refetch current page so counts & pagination stay accurate
      await refetch();
    } catch (error) {
      console.error('Error deleting news:', error);
      toast.error('Haber silinirken hata oluştu');
    } finally {
      setDeleteDialogOpen(false);
      setNewsToDelete(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/giris');
  };

  const handleEdit = (id) => {
    navigate(`/admin/haber/${id}`);
  };

  const handleDeleteClick = (item) => {
    setNewsToDelete(item);
    setDeleteDialogOpen(true);
  };

  const filteredNews = news;

  const stats = {
    total: totalCount,
    featured: featuredCount,
    videos: videoCount,
  };

  const isSearching = Boolean(searchQuery && searchQuery.trim().length >= 2);
  const totalPages = isSearching ? 1 : Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showingFrom = filteredNews.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo = isSearching
    ? filteredNews.length
    : (currentPage - 1) * PAGE_SIZE + filteredNews.length;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-gray-500">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0F172A] text-white p-4 z-50">
        <div className="mb-8">
          <h1 className="text-lg font-bold tracking-tight">HABER TÜRKİYE'DEN</h1>
          <p className="text-gray-400 text-xs mt-1">Yönetim Paneli</p>
        </div>

        <nav className="space-y-1">
          <Link
            to="/admin"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/10 text-white"
          >
            <Newspaper className="w-5 h-5" />
            <span className="font-medium">Haberler</span>
          </Link>
          <Link
            to="/admin/talepler"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="admin-nav-inquiries"
          >
            <Inbox className="w-5 h-5" />
            <span className="flex-1">Gelen Talepler</span>
            {unreadInquiries > 0 && (
              <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px] px-1.5">{unreadInquiries}</Badge>
            )}
          </Link>
          <Link
            to="/admin/reklamlar"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="admin-nav-ads"
          >
            <Megaphone className="w-5 h-5" />
            <span>Reklam Yönetimi</span>
          </Link>
          <Link
            to="/admin/ayarlar"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="admin-nav-settings"
          >
            <SettingsIcon className="w-5 h-5" />
            <span>Site Ayarları</span>
          </Link>

          <div className="pt-4 pb-1">
            <p className="px-3 text-xs uppercase font-bold text-gray-500 tracking-wider">Sayfa Yönetimi</p>
          </div>
          <Link
            to="/admin/sayfa/hakkimizda"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="admin-nav-page-hakkimizda"
          >
            <FileEdit className="w-5 h-5" />
            <span>Hakkımızda</span>
          </Link>
          <Link
            to="/admin/sayfa/reklam-isbirligi"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
            data-testid="admin-nav-page-reklam-isbirligi"
          >
            <FileEdit className="w-5 h-5" />
            <span>Reklam & İş Birliği</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"
          >
            <Home className="w-5 h-5" />
            <span>Siteyi Görüntüle</span>
          </Link>
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="border-t border-gray-700 pt-4 mb-4">
            <p className="text-gray-400 text-sm">{user?.username}</p>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Haber Yönetimi</h2>
            <p className="text-gray-500 text-sm mt-1">Tüm haberleri buradan yönetebilirsiniz</p>
          </div>
          <Link to="/admin/haber/ekle">
            <Button className="bg-[#0284C7] hover:bg-[#0369A1]" data-testid="add-news-btn">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Haber Ekle
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-lg p-5 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-gray-500 text-sm">Toplam Haber</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-5 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Newspaper className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.featured}</p>
                <p className="text-gray-500 text-sm">Öne Çıkan</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg p-5 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Video className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.videos}</p>
                <p className="text-gray-500 text-sm">Video İçerik</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search & Table */}
        <div className="bg-white rounded-lg border border-gray-200 mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Haber ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="admin-search-input"
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[400px]">Başlık</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow key="loading-row">
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      Yükleniyor...
                    </TableCell>
                  </TableRow>
                ) : filteredNews.length === 0 ? (
                  <TableRow key="empty-row">
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      {searchQuery ? 'Aramanızla eşleşen haber bulunamadı' : 'Henüz haber eklenmemiş'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredNews.map((item) => (
                    <NewsRow 
                      key={`news-item-${item.id}`}
                      item={item} 
                      onEdit={handleEdit}
                      onDelete={handleDeleteClick}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination & counter — hidden during search */}
          {!isSearching && totalCount > 0 && (
            <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <p className="text-xs text-gray-500" data-testid="pagination-summary">
                <strong>{showingFrom}–{showingTo}</strong> arası gösteriliyor — toplam <strong>{totalCount}</strong> haber
              </p>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onChange={(p) => setCurrentPage(p)}
              />
            </div>
          )}
        </div>
      </main>

      {/* Delete Dialog - Wrapped in stable div */}
      <div id="delete-dialog-wrapper">
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Haberi Sil</AlertDialogTitle>
              <AlertDialogDescription>
                {newsToDelete ? (
                  <span>"{newsToDelete.title}" başlıklı haberi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</span>
                ) : (
                  <span>Bu haberi silmek istediğinize emin misiniz?</span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
                data-testid="confirm-delete-btn"
              >
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
